import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ConnectedAccountsResponse, EmailProvider } from '@/lib/types'

export function useConnectedAccounts() {
  return useQuery({
    queryKey: ['connected-accounts'],
    queryFn: () => api<ConnectedAccountsResponse>('/api/email/accounts'),
  })
}

export function useConnectEmail() {
  return useMutation({
    mutationFn: async (provider: EmailProvider) => {
      const { url } = await api<{ url: string }>(`/api/email/connect/${provider}`)
      // Open OAuth popup
      const width = 600
      const height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      const popup = window.open(
        url,
        'email-oauth',
        `width=${width},height=${height},left=${left},top=${top}`,
      )

      return new Promise<void>((resolve, reject) => {
        const interval = setInterval(() => {
          try {
            if (!popup || popup.closed) {
              clearInterval(interval)
              resolve()
              return
            }
            const popupUrl = popup.location.href
            if (popupUrl.includes('/api/email/callback/') || popupUrl.includes('code=')) {
              const urlParams = new URLSearchParams(popup.location.search)
              const code = urlParams.get('code')
              clearInterval(interval)
              popup.close()

              if (code) {
                api(`/api/email/callback/${provider}`, {
                  method: 'POST',
                  body: JSON.stringify({ code }),
                })
                  .then(() => resolve())
                  .catch(reject)
              } else {
                reject(new Error('No authorization code received'))
              }
            }
          } catch {
            // Cross-origin error — popup is still on OAuth provider page
          }
        }, 500)

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(interval)
          popup?.close()
          reject(new Error('OAuth flow timed out'))
        }, 5 * 60_000)
      })
    },
  })
}

export function useDisconnectEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) =>
      api(`/api/email/accounts/${accountId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connected-accounts'] })
    },
  })
}

export function useSyncEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) =>
      api<{ synced: number }>(`/api/email/accounts/${accountId}/sync`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connected-accounts'] })
    },
  })
}
